/**
 * Session activity tracking (#517).
 *
 * The in-tab inactivity logout (`useInactivityLogout`) only lives in a browser
 * timer, so closing the tab pauses it and the 30-day refresh cookie silently
 * logs the user back in on return. This module persists the user's last-activity
 * time to `localStorage` so the same idle-timeout policy can be enforced across
 * tab closures: on app mount, if the gap since last activity exceeds the timeout,
 * the session is not restored.
 *
 * `localStorage` (not `sessionStorage`) is deliberate — it survives a tab close
 * and is shared across tabs of the same origin, so activity in any tab keeps the
 * session alive. All access is wrapped so a disabled/throwing store degrades to
 * "no persisted activity" rather than breaking auth.
 *
 * The timeout itself is a global platform setting configured by super/platform
 * admins (#181), fetched from `GET /platform/session-timeout` on app boot via
 * `fetchSessionTimeoutConfig()`. The resolved value is cached in `localStorage`
 * so a returning tab can evaluate `isIdleExpired()` immediately, before that
 * fetch resolves, using the last-known value. `VITE_SESSION_TIMEOUT_MINUTES`
 * (if set) is only the fallback used until the platform value is first fetched.
 *
 * Both the cached and freshly-fetched values are clamped to
 * [MIN_SESSION_TIMEOUT_MINUTES, MAX_SESSION_TIMEOUT_MINUTES] (mirroring the
 * backend bounds) — an out-of-range value (e.g. a tampered/stale localStorage
 * entry) is rejected rather than trusted, so the idle-logout control can't be
 * bypassed by inflating the cached timeout.
 */
import { fetchPublicPlatformConfig } from './publicPlatformConfig'
import { MIN_SESSION_TIMEOUT_MINUTES, MAX_SESSION_TIMEOUT_MINUTES } from '../constants/sessionTimeoutLimits'

export const STORAGE_KEY = 'coformit_last_activity'
export const TIMEOUT_STORAGE_KEY = 'coformit_session_timeout_minutes'

const ENV_DEFAULT_MINUTES = parseInt(import.meta.env.VITE_SESSION_TIMEOUT_MINUTES, 10) || 30

function isInBounds(minutes) {
  return (
    typeof minutes === 'number' &&
    Number.isFinite(minutes) &&
    minutes >= MIN_SESSION_TIMEOUT_MINUTES &&
    minutes <= MAX_SESSION_TIMEOUT_MINUTES
  )
}

function readCachedTimeoutMinutes() {
  try {
    const raw = localStorage.getItem(TIMEOUT_STORAGE_KEY)
    const parsed = parseInt(raw, 10)
    return isInBounds(parsed) ? parsed : null
  } catch {
    return null
  }
}

let sessionTimeoutMinutes = readCachedTimeoutMinutes() ?? ENV_DEFAULT_MINUTES

/**
 * Current effective idle-session timeout, in minutes.
 */
export function getSessionTimeoutMinutes() {
  return sessionTimeoutMinutes
}

/**
 * Current effective idle-session timeout, in milliseconds.
 */
export function getSessionTimeoutMs() {
  return sessionTimeoutMinutes * 60 * 1000
}

/**
 * Adopt a newly-fetched platform-configured timeout (minutes) and persist it
 * so the next load — even before the fetch resolves — uses the same value.
 * Out-of-bounds values are rejected (the current value is left in place),
 * mirroring the backend's [1, 1440] validation.
 */
export function setSessionTimeoutMinutes(minutes) {
  if (!isInBounds(minutes)) return
  sessionTimeoutMinutes = minutes
  try {
    localStorage.setItem(TIMEOUT_STORAGE_KEY, String(minutes))
  } catch {
    // Storage unavailable — in-memory value for this tab is still updated.
  }
}

/**
 * Fetch the platform-wide idle-session timeout (super/platform admin setting,
 * #181) and adopt it via `setSessionTimeoutMinutes`. Public endpoint, no auth
 * required — safe to call before login. The underlying request is bounded by
 * a timeout (see `fetchPublicPlatformConfig`), so a stalled request (proxy
 * black-hole, cold start) can't hang a caller that awaits this. Fails silently,
 * leaving the cached or default value in place, on any error/timeout/non-2xx.
 */
export async function fetchSessionTimeoutConfig() {
  const data = await fetchPublicPlatformConfig('/platform/session-timeout')
  if (typeof data?.sessionTimeoutMinutes === 'number') {
    setSessionTimeoutMinutes(data.sessionTimeoutMinutes)
  }
}

/**
 * Record that the user was active at `now` (defaults to the current time).
 */
export function recordActivity(now = Date.now()) {
  try {
    localStorage.setItem(STORAGE_KEY, String(now))
  } catch {
    // Storage unavailable (private mode / quota / disabled) — nothing to persist.
  }
}

/**
 * Read the last-activity timestamp, or `null` if none is stored or it is invalid.
 */
export function getLastActivity() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const ts = parseInt(raw, 10)
    return Number.isFinite(ts) ? ts : null
  } catch {
    return null
  }
}

/**
 * Remove the stored last-activity timestamp (on logout / idle expiry).
 */
export function clearLastActivity() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

/**
 * True only when a last-activity timestamp exists AND more than the session
 * timeout has elapsed since it. An absent timestamp returns false so the normal
 * refresh flow decides — this fails open rather than locking users out.
 */
export function isIdleExpired(now = Date.now()) {
  const last = getLastActivity()
  if (last === null) return false
  return now - last > getSessionTimeoutMs()
}
